import React from "react";
import {Typography} from "@mui/material";
import {PlatformTable} from "../PlatformTable";
import {withAppContext} from "../../util/withAppContext";
import withBreakpoint from "../../util/withBreakpoint";
import {Blue3, Grey7} from "../../theme/styles";
import {statusPill} from "../../util/statusPill";
import {focused} from "../../util/documentSearch";

/**
 * The documents list as the suite's standard grid, with the three cells this list needs.
 *
 * A SUBCLASS, WHICH IS HOW THE SUITE EXTENDS A TABLE -- t1's ObjectRevisionTable does exactly this
 * for the same object. The base PlatformTable is identical in t1, t4, t5 and here, and must stay
 * that way; a renderer only this list wants belongs in this list's component.
 *
 * THE PER-ROW ACTION MENU IS GONE FROM THIS TABLE, deliberately: a list is for comparing documents
 * and the column it occupied is better spent on the document number. The lifecycle actions live on
 * the cards, where there is room to label them, and on the document itself.
 *
 * It is also the only way to put a control in a cell at all. The base table builds every column's
 * renderCell from a NAME looked up in getCellRenderer, so a caller cannot hand it a function --
 * which is why the row menu first went in as a hidden button beside the grid, where nobody could
 * reach it.
 */
class DocumentsTable extends PlatformTable {

    getCellRenderer(name) {
        if (name === "Document Name Cell") {
            return this.renderDocumentNameCell;
        }
        if (name === "Status Pill Cell") {
            return this.renderStatusPillCell;
        }
        return super.getCellRenderer(name);
    }

    /**
     * Refresh means FETCH AGAIN, which this table cannot do for itself.
     *
     * The base handler invalidates the component and re-queries it -- and this component's query
     * reads the rows the page handed it, so the base behaviour would re-render exactly what is
     * already on screen. A Refresh that cannot show you a document somebody else just released is
     * worse than no Refresh at all, so it goes back to the page, which owns the fetch.
     */
    handleMenuClick(identifier) {
        if (identifier === "Refresh" && this.props.onRefresh) {
            this.props.onRefresh();
            return;
        }
        super.handleMenuClick(identifier);
    }

    /**
     * One attribute of the row behind a grid cell, from wherever it can be had.
     *
     * THE GRID ROW HOLDS ONLY THE PLACED COLUMNS. getRows writes a key per displayed column and
     * nothing else, so reading Status off params.row works -- it is a column -- while reading USID
     * off it gives undefined, silently, and every menu then acts on nothing. The buffer is the
     * record, so unplaced attributes come from there.
     *
     * params.row is consulted first only so a placed column still reads correctly while a
     * background fetch is swapping the buffer out.
     */
    readAttribute = (params, field) => {
        const row = params?.row;
        if (row && row[field] !== undefined && row[field] !== null) {
            return row[field];
        }
        const record = this.state.buffer?.[row?.id];
        return (record?.get ? record.get(field) : undefined) ?? null;
    };

    // Single-click a row to open the document, which is what a list of documents is for. The menu
    // button stops propagation, so it still opens without also opening the document.
    onRowClick(params) {
        const usid = this.readAttribute(params, "USID");
        if (usid && this.props.onOpen) {
            this.props.onOpen(usid);
            return;
        }
        super.onRowClick(params);
    }

    // The name is the row's link into the document, because linking the noun is how links work --
    // you click the procedure's name to read the procedure.
    renderDocumentNameCell = (___, params, __, _) => {
        const name = this.readAttribute(params, "Name");
        const usid = this.readAttribute(params, "USID");

        // A cell renderer fills a 48px row, so its content sits at the TOP unless the wrapper
        // claims the full height and centres in it.
        const cell = (content) => (
            <div style={{display: "flex", alignItems: "center", height: "100%", overflow: "hidden"}}>{content}</div>
        );

        if (!usid) {
            return cell(<Typography style={{fontSize: 14, color: Grey7}}>{name || "—"}</Typography>);
        }

        // THE MATCHED SENTENCE, UNDER THE NAME, AND ONLY WHILE A SEARCH IS RUNNING. The preview is
        // still not a column -- a column of truncated prose would push the name off every row of an
        // ordinary list, which is the reason it was left out. During a search it is the reason the
        // row is there at all, so a reader needs to see which words matched without opening it.
        const snippet = this.readAttribute(params, "Snippet");

        const link = (
            <Typography
                onClick={() => this.props.onOpen && this.props.onOpen(usid)}
                style={{fontSize: 14, fontWeight: 500, color: Blue3, cursor: "pointer",
                        // EXPLICIT, BECAUSE TWO LINES HAVE TO FIT. The row is 33px -- compact
                        // density, not the applet's 48 -- so the name at its default leading plus a
                        // snippet under it overflowed, and the cell clips rather than growing.
                        lineHeight: "17px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
            >{/* An untitled document is still openable, so fall back to a dash rather than
                   rendering nothing clickable. */}
                {name || "Untitled"}
            </Typography>
        );

        if (!snippet) return cell(link);

        return cell(
            <div style={{overflow: "hidden", width: "100%"}}>
                {link}
                {/* Runs, not HTML: ts_headline leaves the document's own text unescaped. */}
                <div style={{fontSize: 10, lineHeight: "12px", color: Grey7, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {focused(snippet).map((part, at) => (
                        <span key={at}
                              style={part.hit ? {backgroundColor: "#FFF3B0", fontWeight: 700} : undefined}>
                            {part.text}
                        </span>))}
                </div>
            </div>
        );
    };

    renderStatusPillCell = (___, params, __, _) => statusPill(this.readAttribute(params, "Status"));

}

export default withBreakpoint(withAppContext(DocumentsTable));
